import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * イベント API 操作クラス
 */
export default class Events {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET Event
   */
  public async get(userStore: LoginUser, eventId: number): Promise<EventGetModel> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      eventId: eventId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Event, formatParams);
    const response = await this.commonService.executeGet<EventGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
  /**
   * GET Events
   */
  public async gets(
    userStore: LoginUser,
    queryParams?: {
      startIndex?: number;
      endIndex?: number;
      sortKey?: string;
      order?: string;
      status?: string;
      needFiltering?: string;
    }
  ): Promise<Array<EventGetModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Events, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<EventGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET EventsCount
   */
  public async getCount(
    userStore: LoginUser,
    queryParams?: {
      status: string;
      needFiltering?: string;
    }
  ): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Events, formatParams);
    const params: any = queryParams ? queryParams : {};
    // countの場合は末尾に「count」をつける
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * POST Events
   */
  public async post(userStore: LoginUser, requestBody: EventPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Events, formatParams);

    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH Events
   */
  public async patch(userStore: LoginUser, eventId: number, requestBody: EventPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      eventId: eventId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Event, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE Events
   */
  public async delete(userStore: LoginUser, eventId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      eventId: eventId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Event, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface EventGetModel {
  /** 施設を特定する ID */
  facilityId: number;
  /** イベントを特定する ID */
  id: number;
  /** イベントの公開(true)/非公開(false)の情報 */
  isPublic: boolean;
  /** イベント実施日(YYYYMMDDhhmmssSSS) */
  date: number;
  /**  */
  dayOfWeek: string;
  /** イベント時間開始(YYYYMMDDhhmmssSSS) */
  timeFrom: number;
  /** イベント時間終了(YYYYMMDDhhmmssSSS) */
  timeTo: number;
  /** 宛先種別 */
  targetType: EventTargetType;
  /** 宛先 */
  targets: number[];
  /** 返信が必要か（true：必要、false：不要） */
  needReply: boolean;
  /** 参加申込み締切日時 */
  replyDeadline?: number;
  /** 実施種別 */
  holdType: EventHoldType;
  /** イベントの開催場所 */
  place: string;
  /** イベントのタイトル */
  title: string;
  /** イベントの内容 */
  description: string;
  /** イベントに利用する S3 のリンク */
  imageUrl: string;
  /** オンライン配信用トークンUUID */
  meetingToken?: string;
  /** イベントの編集者 */
  writtenBy: string;
  /** イベントの編集者のuserId */
  writerId: string;
  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** イベントの登録日 */
  created: number;
  /** イベントの更新日 */
  updated: number;
  /**  */
  sortkeyDate?: number;
  /** Base64変換後の画像 */
  convertedImage?: string;
}

export enum EventTargetType {
  UNKNOWN = -1,
  ALL = 0,
  RESIDENT_GROUP = 1
}

export enum EventHoldType {
  UNKNOWN = -1,
  // 現地とオンライン
  LOCAL_AND_ONLINE = 0,
  // 現地のみ
  LOCAL_ONLY = 1,
  // オンラインのみ
  ONLINE_ONLY = 2
}

export interface EventPostModel {
  /** イベント実施日 */
  date: number;
  /** イベント開始日時 */
  timeFrom: number;
  /** イベント終了日時 */
  timeTo: number;
  /** 宛先種別 */
  targetType: EventTargetType;
  /** 宛先 */
  targets: number[];
  /** 返信が必要か（true：必要、false：不要） */
  needReply: boolean;
  /** 参加申込み締切日時 */
  replyDeadline?: number;
  /** 実施種別 */
  holdType: EventHoldType;
  /** 場所 */
  place: string;
  /** タイトル */
  title: string;
  /** イベントの内容 */
  description: string;
  /** 画像URL */
  image: string | undefined;
  /** オンライン配信用トークンUUID */
  meetingToken?: string;
  /** 記載者 */
  writtenBy: string;
  /** 記載者ID */
  writerId: string;
}

export interface EventPatchModel {
  /** イベントの公開設定 */
  isPublic: boolean;
  /** イベント実施日 */
  date: number;
  /** イベント開始日時 */
  timeFrom: number;
  /** イベント終了日時 */
  timeTo: number;
  /** 宛先種別 */
  targetType: EventTargetType;
  /** 宛先 */
  targets: number[];
  /** 返信が必要か（true：必要、false：不要） */
  needReply: boolean;
  /** 参加申込み締切日時 */
  replyDeadline?: number;
  /** 実施種別 */
  holdType: EventHoldType;
  /** 場所 */
  place: string;
  /** タイトル */
  title: string;
  /** イベントの内容 */
  description: string;
  /** 画像URL */
  image: string | undefined;
  /** オンライン配信用トークンUUID */
  meetingToken?: string;
  /** 記載者 */
  writtenBy: string;
  /** 記載者ID */
  writerId: string;
  /** 更新日時 */
  updated: number;
}
